import { FC, useCallback, useMemo, useState } from 'react';
import { Button, ComboBox, InlineNotification, TextInput } from '@carbon/react';
import { useForm } from '@tanstack/react-form';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import type { ApiPayloads } from 'types/api-payloads';
import type {
  FormationManagementFacets,
  GeologicIntervalValidationError,
} from 'types/api-responses';
import { ColorSwatch } from 'components/color-picker';
import { formatResource } from 'utils/misc';
import {
  useCanonicalDefaultsAtom,
  createGeologicIntervalAtom,
  closeGeologicIntervalDrawerAtom,
  useBasinIntervalsSearchAtom,
  useLithologyItems,
  type CanonicalDefault,
} from './atoms';
import { GeologicIntervalValidations } from './geologic-interval-validations';
import {
  boundFromCanonical,
  computeLowerBoundOptions,
  computeUpperBoundOptions,
  findAdjacentAboveBound,
  findAdjacentBelowBound,
  formatIdx,
  getBoundExtremeError,
  isDuplicateName,
  isValidHexColor,
  toTitleCaseBasin,
  toTitleCaseBasinLiveInput,
} from './utils';
import {
  OTHER_BASIN_ID,
  type AddGeologicIntervalFormValues,
  type BasinItem,
  type GeologicIntervalBoundItem,
} from './types';
import styles from './geologic-interval-drawer.module.scss';

type CreateGeologicIntervalBody =
  ApiPayloads['createGeologicInterval']['payload']['geologicInterval'];

const DEFAULT_VALUES: AddGeologicIntervalFormValues = {
  basin: null,
  customBasin: '',
  intervalName: '',
  age: '',
  lithology: '',
  upperBound: null,
  lowerBound: null,
  displayColor: '#000000',
};

export interface AddGeologicIntervalContentProps {
  facets: FormationManagementFacets | undefined;
  onCreated: () => void;
}

// Add form. Progressive disclosure: basin → name → details → positioning →
// color. "Other…" basin mode swaps the bound source to canonical defaults.
// Save first probes /validations/geologic_intervals; only zero-error runs
// commit via POST /geologic_intervals.
export const AddGeologicIntervalContent: FC<
  AddGeologicIntervalContentProps
> = ({ facets, onCreated }) => {
  const [createState, createInterval] = useAtom(createGeologicIntervalAtom);
  const closeDrawer = useSetAtom(closeGeologicIntervalDrawerAtom);

  const canonicalDefaultsAtom = useCanonicalDefaultsAtom();
  const canonicalDefaultsResource = useAtomValue(canonicalDefaultsAtom);
  const { data: canonicalDefaultsData } = formatResource(
    canonicalDefaultsResource
  );
  const canonicalDefaults: CanonicalDefault[] | null = Array.isArray(
    canonicalDefaultsData
  )
    ? canonicalDefaultsData
    : null;
  // Memoized so each bound keeps one identity across renders: the bound
  // comboboxes auto-fill each other, and Carbon fires `onChange` whenever
  // `selectedItem` changes identity, so fresh objects ping-pong forever.
  const canonicalBounds = useMemo<GeologicIntervalBoundItem[]>(
    () =>
      (canonicalDefaults ?? [])
        .map(boundFromCanonical)
        .sort((a, b) => a.positionalIndex - b.positionalIndex),
    [canonicalDefaults]
  );

  const basinIntervalsSearch = useBasinIntervalsSearchAtom();
  const [basinIntervalsState, setBasinIntervalsPayload] = useAtom(
    basinIntervalsSearch.searchAtom
  );
  const resetBasinIntervals = useSetAtom(
    basinIntervalsSearch.resetSearchDataAtom
  );

  const lithologyItems = useLithologyItems();

  const isSubmitting = createState.loading;
  // Local error state so reopening the drawer always starts clean; the
  // mutation atom retains the last result across mounts.
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [liveValidationErrors, setLiveValidationErrors] = useState<
    GeologicIntervalValidationError[]
  >([]);

  const basinItems = useMemo<BasinItem[]>(() => {
    const fromFacets = (facets?.basins ?? []).map((b) => ({ id: b, text: b }));
    return [...fromFacets, { id: OTHER_BASIN_ID, text: 'Other…' }];
  }, [facets]);

  const basinIntervals = useMemo<GeologicIntervalBoundItem[]>(() => {
    const rows = basinIntervalsState.data?.results ?? [];
    return rows
      .filter((r) => r.positionalIndex !== null)
      .map((r) => ({
        id: String(r.id),
        name: r.geologicInterval,
        text: `${r.geologicInterval} (idx ${formatIdx(
          r.positionalIndex as number
        )})`,
        positionalIndex: r.positionalIndex as number,
      }))
      .sort((a, b) => a.positionalIndex - b.positionalIndex);
  }, [basinIntervalsState.data]);

  const form = useForm({
    defaultValues: DEFAULT_VALUES,
    onSubmit: ({ value }) => {
      setSubmitError(null);
      const isOther = value.basin?.id === OTHER_BASIN_ID;
      const basinName = isOther
        ? toTitleCaseBasin(value.customBasin)
        : (value.basin?.text ?? '');
      const body: CreateGeologicIntervalBody = {
        basinName,
        geologicInterval: value.intervalName.trim().toUpperCase(),
        ...(value.age.trim() ? { age: Number(value.age) } : {}),
        ...(value.lithology ? { lithology: value.lithology } : {}),
        ...(value.upperBound ? { upperBoundName: value.upperBound.name } : {}),
        ...(value.lowerBound ? { lowerBoundName: value.lowerBound.name } : {}),
        ...(isValidHexColor(value.displayColor)
          ? { displayColor: value.displayColor }
          : {}),
        ...(isOther ? { seedCanonicalDefaults: true } : {}),
      };
      createInterval({
        type: 'POST',
        url: '/geologic_intervals',
        data: { geologicInterval: body },
        onSuccess: () => {
          onCreated();
          closeDrawer();
        },
        onError: (errorData) => {
          const firstError = errorData?.errors?.[0];
          setSubmitError(
            firstError?.message ?? 'Failed to create geologic interval.'
          );
        },
      });
    },
  });

  const resetDependentFields = useCallback(() => {
    form.setFieldValue('customBasin', '');
    form.setFieldValue('intervalName', '');
    form.setFieldValue('age', '');
    form.setFieldValue('lithology', '');
    form.setFieldValue('upperBound', null);
    form.setFieldValue('lowerBound', null);
    form.setFieldValue('displayColor', DEFAULT_VALUES.displayColor);
    resetBasinIntervals(undefined);
  }, [form, resetBasinIntervals]);

  return (
    <form
      className={styles.formContainer}
      onSubmit={(e) => {
        e.preventDefault();
        form.handleSubmit();
      }}
    >
      <form.Subscribe selector={(s) => s.values}>
        {(values) => {
          const isOther = values.basin?.id === OTHER_BASIN_ID;
          const basinName = isOther
            ? toTitleCaseBasin(values.customBasin)
            : (values.basin?.text ?? '');
          const basinDuplicate =
            isOther &&
            isDuplicateName(values.customBasin, facets?.basins ?? []);
          const basinReady =
            basinName.length > 0 &&
            !basinDuplicate &&
            (!isOther || values.customBasin.trim().length > 0);

          const nameReady = basinReady && values.intervalName.trim().length > 0;

          const boundsSource: GeologicIntervalBoundItem[] = isOther
            ? canonicalBounds
            : basinIntervals;
          const hasBoundContext = boundsSource.length > 0;

          // Client-side duplicate check for instant feedback; the server-side
          // validations probe runs in parallel and is authoritative.
          const nameDuplicate =
            nameReady &&
            !isOther &&
            isDuplicateName(
              values.intervalName,
              boundsSource.map((i) => i.name)
            );

          const intervalNameLiveError = liveValidationErrors.find(
            (e) => e.attribute === 'geologic_interval'
          );
          const basinLiveError = liveValidationErrors.find(
            (e) => e.attribute === 'basin_name' || e.attribute === 'base'
          );
          const intervalNameInvalid =
            nameDuplicate || Boolean(intervalNameLiveError);
          const intervalNameInvalidText =
            intervalNameLiveError?.message ??
            `"${values.intervalName}" already exists in ${basinName}.`;

          const upperOptions = computeUpperBoundOptions(
            boundsSource,
            values.lowerBound
          );
          const lowerOptions = computeLowerBoundOptions(
            boundsSource,
            values.upperBound
          );
          const boundExtremeError = getBoundExtremeError(
            boundsSource,
            values.upperBound,
            values.lowerBound
          );

          const colorValid = isValidHexColor(values.displayColor);

          const canSubmit =
            nameReady &&
            !intervalNameInvalid &&
            !basinLiveError &&
            colorValid &&
            (!hasBoundContext ||
              (Boolean(values.upperBound) &&
                Boolean(values.lowerBound) &&
                !boundExtremeError));

          return (
            <>
              <GeologicIntervalValidations
                basinName={basinName}
                intervalName={values.intervalName}
                isOther={isOther}
                onResult={setLiveValidationErrors}
              />
              {submitError && (
                <div className={styles.errorBanner}>
                  <InlineNotification
                    kind="error"
                    title="Save failed"
                    subtitle={submitError}
                    onCloseButtonClick={() => setSubmitError(null)}
                    lowContrast
                  />
                </div>
              )}
              <div className={styles.scrollableBody}>
                <section className={styles.section}>
                  <form.Field name="basin">
                    {(field) => (
                      <ComboBox
                        id="add-basin"
                        titleText="Basin (required)"
                        placeholder="Search basins…"
                        items={basinItems}
                        itemToString={(item: BasinItem | null) =>
                          item?.text ?? ''
                        }
                        selectedItem={field.state.value}
                        onChange={({ selectedItem }) => {
                          field.handleChange(selectedItem ?? null);
                          resetDependentFields();
                          if (
                            selectedItem &&
                            selectedItem.id !== OTHER_BASIN_ID
                          ) {
                            setBasinIntervalsPayload(() => ({
                              paginated: false,
                              basins: [selectedItem.text],
                            }));
                          }
                        }}
                        size="md"
                      />
                    )}
                  </form.Field>
                  {isOther && (
                    <form.Field name="customBasin">
                      {(field) => (
                        <TextInput
                          id="add-basin-custom"
                          labelText="Basin Name (required)"
                          placeholder="Enter basin name…"
                          helperText="Saving this interval will generate a new basin for future geologic intervals."
                          value={field.state.value}
                          onChange={(e) =>
                            field.handleChange(
                              toTitleCaseBasinLiveInput(e.target.value)
                            )
                          }
                          invalid={basinDuplicate}
                          invalidText="This basin name already exists."
                          size="md"
                        />
                      )}
                    </form.Field>
                  )}
                </section>

                <section className={styles.section}>
                  <form.Field name="intervalName">
                    {(field) => (
                      <TextInput
                        id="add-interval-name"
                        labelText="Geologic Interval Name (required)"
                        placeholder="e.g. Wolfcamp A"
                        value={field.state.value}
                        onChange={(e) =>
                          field.handleChange(e.target.value.toUpperCase())
                        }
                        invalid={intervalNameInvalid}
                        invalidText={intervalNameInvalidText}
                        disabled={!basinReady}
                        size="md"
                      />
                    )}
                  </form.Field>
                </section>

                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>Interval Details</h3>
                  <div className={styles.fieldRow}>
                    <form.Field name="age">
                      {(field) => (
                        <TextInput
                          id="add-age"
                          labelText="Age (MYA, optional)"
                          placeholder="e.g. 280"
                          value={field.state.value}
                          onChange={(e) =>
                            field.handleChange(
                              e.target.value.replace(/[^\d.]/g, '')
                            )
                          }
                          disabled={!basinReady}
                          size="md"
                        />
                      )}
                    </form.Field>
                    <form.Field name="lithology">
                      {(field) => (
                        <ComboBox
                          id="add-lithology"
                          titleText="Lithology (optional)"
                          placeholder="Search lithologies…"
                          items={lithologyItems}
                          itemToString={(item) => item ?? ''}
                          selectedItem={field.state.value || null}
                          onChange={({ selectedItem }) =>
                            field.handleChange(selectedItem ?? '')
                          }
                          disabled={!basinReady}
                          size="md"
                        />
                      )}
                    </form.Field>
                  </div>
                </section>

                {basinReady && hasBoundContext && (
                  <section className={styles.section}>
                    <h3 className={styles.sectionTitle}>
                      Interval Positioning
                    </h3>
                    <p className={styles.sectionHelp}>
                      {isOther
                        ? 'Pick the upper / lower bounds from the standard interval set. Saving creates these in the new basin plus this row.'
                        : 'Pick the bounding intervals for the new entry.'}
                    </p>
                    <div className={styles.fieldStack}>
                      <form.Field name="upperBound">
                        {(field) => (
                          <ComboBox
                            id="add-upper-bound"
                            titleText="Upper Bound"
                            placeholder="Search intervals…"
                            items={upperOptions}
                            itemToString={(
                              item: GeologicIntervalBoundItem | null
                            ) => item?.text ?? ''}
                            selectedItem={field.state.value}
                            onChange={({ selectedItem }) => {
                              field.handleChange(selectedItem ?? null);
                              // Auto-fill adjacent-below so the new interval
                              // slots into exactly one stratigraphic gap.
                              const below = findAdjacentBelowBound(
                                boundsSource,
                                selectedItem ?? null
                              );
                              // By id, so a rebuilt bound object can't set off
                              // the other picker's auto-fill in turn.
                              if (
                                below &&
                                below.id !==
                                  form.getFieldValue('lowerBound')?.id
                              )
                                form.setFieldValue('lowerBound', below);
                            }}
                            invalid={!field.state.value}
                            invalidText="Upper Bound selection is required."
                            size="md"
                          />
                        )}
                      </form.Field>
                      <form.Field name="lowerBound">
                        {(field) => (
                          <ComboBox
                            id="add-lower-bound"
                            titleText="Lower Bound"
                            placeholder="Search intervals…"
                            items={lowerOptions}
                            itemToString={(
                              item: GeologicIntervalBoundItem | null
                            ) => item?.text ?? ''}
                            selectedItem={field.state.value}
                            onChange={({ selectedItem }) => {
                              field.handleChange(selectedItem ?? null);
                              const above = findAdjacentAboveBound(
                                boundsSource,
                                selectedItem ?? null
                              );
                              if (
                                above &&
                                above.id !==
                                  form.getFieldValue('upperBound')?.id
                              )
                                form.setFieldValue('upperBound', above);
                            }}
                            invalid={!field.state.value}
                            invalidText="Lower Bound selection is required."
                            size="md"
                          />
                        )}
                      </form.Field>
                      {boundExtremeError && (
                        <InlineNotification
                          kind="error"
                          title="Pick a different bound"
                          subtitle={boundExtremeError}
                          hideCloseButton
                          lowContrast
                        />
                      )}
                      {!boundExtremeError &&
                        values.upperBound &&
                        values.lowerBound && (
                          <p className={styles.indexPreview}>
                            Calculated Positional Index:{' '}
                            <strong>
                              {formatIdx(
                                (values.upperBound.positionalIndex +
                                  values.lowerBound.positionalIndex) /
                                  2
                              )}
                            </strong>
                          </p>
                        )}
                    </div>
                  </section>
                )}

                <section className={styles.section}>
                  <h3 className={styles.sectionTitle}>Display Color</h3>
                  <form.Field name="displayColor">
                    {(field) => {
                      const valid = isValidHexColor(field.state.value);
                      return (
                        <div className={styles.colorRow}>
                          <ColorSwatch
                            color={valid ? field.state.value : '#000000'}
                            onChange={field.handleChange}
                          />
                          <span className={styles.hexLabel}>
                            {valid ? field.state.value : '—'}
                          </span>
                        </div>
                      );
                    }}
                  </form.Field>
                </section>
              </div>

              <div className={styles.footer}>
                <Button kind="secondary" type="button" onClick={closeDrawer}>
                  Cancel
                </Button>
                <Button
                  kind="primary"
                  type="submit"
                  disabled={!canSubmit || isSubmitting}
                >
                  {isSubmitting ? 'Saving…' : 'Save'}
                </Button>
              </div>
            </>
          );
        }}
      </form.Subscribe>
    </form>
  );
};
